import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import { SearchableSelect } from "../ui/searchableSelect.tsx";
import { buildReportTimezoneOptions } from "../../util/timezoneOptions.ts";
import { CULTURES, FILE_TYPES } from "./exportDialog.tsx";
import { parseColumnDescJson } from "./dataGrid.tsx";
import { ScheduleFilterEditor } from "./scheduleFilterEditor.tsx";
import { ColumnConfig } from "./columnConfig.tsx";
import type { InputParam, InputParamSE, SortOrder, ExportDataFileType } from "../../types/grid.ts";
import type { ScheduledExport } from "../../api/scheduledExport.ts";
import type { WebAdminPageData } from "../../types/session.ts";
import { getClientTimeZone } from "../../util/clientEnv.ts";
import { getDbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import styles from "./dataGrid.module.scss";
import colStyles from "./scheduleExportForm.module.scss";

export interface ScheduleExportFormHandle {
  submit: () => void;
}

const INTERVALS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "weekdays", label: "Specific weekdays" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const WEEKDAYS = [
  { value: "01", label: "Sunday" },
  { value: "02", label: "Monday" },
  { value: "03", label: "Tuesday" },
  { value: "04", label: "Wednesday" },
  { value: "05", label: "Thursday" },
  { value: "06", label: "Friday" },
  { value: "07", label: "Saturday" },
];

// Mask labels for the "weekdays" interval. Index 0 = Sunday … 6 = Saturday,
// matching the server which indexes the mask by DateTimeOffset.DayOfWeek.
const MASK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_MASK = "0111110"; // Mon–Fri

// IANA timezone ids for the schedule-timezone picker (DST-aware firing).
const IANA_ZONES: string[] = (() => {
  try {
    const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    return f ? f("timeZone") : [];
  } catch { return []; }
})();

// ── Parse helpers ────────────────────────────────────────

interface ParsedExportArgs {
  Name: string;
  FilterList: InputParamSE[];
  OrderList: SortOrder[];
  Culture: string;
  Timezone: string;
  FileType: ExportDataFileType;
  /** null = legacy schedule saved without an explicit column selection. */
  ColumnList: string[] | null;
}

function parseExportArgs(json: string): ParsedExportArgs {
  try {
    const p = JSON.parse(json || "{}");
    return {
      Name: p.Name ?? "",
      FilterList: Array.isArray(p.FilterList) ? p.FilterList : [],
      OrderList: Array.isArray(p.OrderList) ? p.OrderList : [],
      Culture: p.Culture ?? "en-US",
      Timezone: p.Timezone ?? getClientTimeZone(),
      FileType: p.FileType ?? "CSV",
      ColumnList: Array.isArray(p.ColumnList) ? p.ColumnList.filter((c: unknown) => typeof c === "string") : null,
    };
  } catch {
    return { Name: "", FilterList: [], OrderList: [], Culture: "en-US", Timezone: getClientTimeZone(), FileType: "CSV", ColumnList: null };
  }
}

interface ParsedSchedule {
  interval: string;
  hour: string;
  day: string;
  weekday: string;
  month: string;
  offset: string;
  mask: string;
}

function parseSchedule(schedule: string, interval: string): ParsedSchedule {
  const defaultOffset = (() => {
    const m = new Date().getTimezoneOffset();
    const sign = m <= 0 ? "+" : "-";
    return `${sign}${String(Math.floor(Math.abs(m) / 60)).padStart(2, "0")}`;
  })();

  const result: ParsedSchedule = { interval, hour: "05", day: "01", weekday: "02", month: "01", offset: defaultOffset, mask: DEFAULT_MASK };
  if (!schedule) return result;

  // Extract offset from end: +HH:MM or -HH:MM
  const offsetMatch = schedule.match(/([+-]\d{2}):\d{2}\s*$/);
  if (offsetMatch) result.offset = offsetMatch[1]!;

  // Extract time HH:MM:SS
  const timeMatch = schedule.match(/(\d{2}):\d{2}:\d{2}/);
  if (timeMatch) result.hour = timeMatch[1]!;

  switch (interval) {
    case "weekly": {
      const m = schedule.match(/^(\d{2})\s/);
      if (m) result.weekday = m[1]!;
      break;
    }
    case "monthly": {
      const m = schedule.match(/^(\d{2})\s/);
      if (m) result.day = m[1]!;
      break;
    }
    case "yearly": {
      const m = schedule.match(/^(\d{2})-(\d{2})\s/);
      if (m) { result.month = m[1]!; result.day = m[2]!; }
      break;
    }
    case "weekdays": {
      const m = schedule.match(/^([01]{7})\s/);
      if (m) result.mask = m[1]!;
      break;
    }
  }

  return result;
}

function buildSchedule(interval: string, hour: string, day: string, weekday: string, month: string, offset: string, mask: string): string {
  const time = `${hour}:00:00 ${offset}:00`;
  switch (interval) {
    case "weekly": return `${weekday} ${time}`;
    case "weekdays": return `${mask} ${time}`;
    case "monthly": return `${day} ${time}`;
    case "yearly": return `${month}-${day} ${time}`;
    default: return time;
  }
}

const OPERATION_TYPES = new Set(["VIEW", "FN", "SP", "EXEC", "IMP"]);

// ── Searchable report selector ───────────────────────────

interface ReportOption { value: string; label: string }

function ReportSearchSelect({ reports, value, onChange, disabled }: {
  reports: ReportOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const selected = reports.find(r => r.value === value);
  const [search, setSearch] = useState(selected?.label ?? "");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sync display text when value changes externally
  useEffect(() => {
    const match = reports.find(r => r.value === value);
    if (match) setSearch(match.label);
  }, [value, reports]);

  const filtered = useMemo(() => {
    if (!search.trim()) return reports;
    const lc = search.toLowerCase();
    return reports.filter(r => r.label.toLowerCase().includes(lc));
  }, [reports, search]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <input
        type="text"
        className={styles.exportSelect}
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search reports..."
        disabled={disabled}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          maxHeight: 200,
          overflowY: "auto",
          background: "var(--color-bg)",
          border: "1px solid var(--color-border-default)",
          borderRadius: "var(--radius-1)",
          boxShadow: "var(--shadow-md)",
          zIndex: "var(--z-dropdown)",
        }}>
          {filtered.map(r => (
            <div
              key={r.value}
              onClick={() => { onChange(r.value); setSearch(r.label); setOpen(false); }}
              style={{
                padding: "var(--space-1) var(--space-2)",
                fontSize: "var(--text-sm)",
                cursor: "pointer",
                background: r.value === value ? "var(--color-primary-muted)" : undefined,
              }}
              onMouseEnter={e => { if (r.value !== value) (e.target as HTMLElement).style.background = "var(--color-bg-muted)"; }}
              onMouseLeave={e => { (e.target as HTMLElement).style.background = r.value === value ? "var(--color-primary-muted)" : ""; }}
            >
              {r.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Form component ───────────────────────────────────────

/** Column-chooser context from the grid the schedule was opened from. */
export interface ScheduleGridColumns {
  /** Full choosable column set in display order (DataGrid's chooserColumns). */
  columns: string[];
  /** The grid's currently visible export columns — the default-checked set. */
  visible: string[];
  /** The report's default visible set — target of the chooser's "Default" button. */
  defaultVisible: string[];
  displayNames?: Record<string, string>;
  tooltips?: Record<string, string>;
  groups?: Record<string, string[]>;
}

export interface ScheduleExportFormProps {
  /** Initial ScheduledExport data (null for brand new from grid). */
  item: ScheduledExport | null;
  /** If provided, used as the report name and made readonly. */
  fixedReportName?: string;
  /** Initial filters from the grid (only used when creating from grid). */
  initialGridFilters?: InputParam[];
  /** Initial sorts from the grid. */
  initialGridSorts?: SortOrder[];
  /** Column-chooser context — present only when opened from a grid. */
  gridColumns?: ScheduleGridColumns;
  /** Called with the assembled ScheduledExport ready to save. */
  onSave: (item: ScheduledExport) => void;
  /** Currently saving. */
  saving?: boolean;
  /** Show status fields (LastRun, NextRun, Suspended) for existing items. */
  showStatus?: boolean;
  disabled?: boolean;
}

export const ScheduleExportForm = forwardRef<ScheduleExportFormHandle, ScheduleExportFormProps>(function ScheduleExportForm({
  item,
  fixedReportName,
  initialGridFilters,
  initialGridSorts,
  gridColumns,
  onSave,
  saving = false,
  showStatus = false,
  disabled = false,
}, ref) {
  const { cache, isSuperAdmin, canRead } = useAuth();
  // Soft-deploy gate for the IANA schedule-timezone feature. When off, the picker
  // is hidden for new schedules and shown read-only for ones that already carry a
  // zone (so the value stays visible + preserved on save). Remove this gate once
  // the feature is validated.
  const tzEditEnabled = getDbFeatureFlags(cache).ScheduleTimeZoneEdit === true;
  // Sibling soft-deploy gate for the "Specific weekdays" interval. When off, the
  // option is hidden for new schedules and the day checkboxes are read-only for
  // ones already on it. The backend worker fires weekdays rows regardless — this
  // only gates creation in the client. Remove once validated.
  const weekdaysEnabled = getDbFeatureFlags(cache).ScheduleWeekdaysEdit === true;

  // Available reports for the dropdown — only ones the caller (or the impersonated user) may
  // read; PageInfo is the endpoint's catalogue, not a grant.
  const availableReports = useMemo(() => {
    if (!cache?.PageInfo?.Page) return [];
    return cache.PageInfo.Page
      .filter(p => p.Enabled && p.Available && OPERATION_TYPES.has(p.Type?.toUpperCase()) && canRead(p.RowID))
      .map(p => ({
        value: p.Name.toLowerCase(),
        label: `${p.DisplayName} (#${p.RowID}, ${p.Name})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cache?.PageInfo?.Page, canRead]);

  // Parse ExportArgs from existing item, or use grid filters for new
  const parsedArgs = useMemo(() => item?.ExportArgs ? parseExportArgs(item.ExportArgs) : null, [item?.ExportArgs]);

  const [title, setTitle] = useState(item?.Title ?? "");
  const [reportName, setReportName] = useState(fixedReportName ?? item?.ReportName ?? "");
  const [additionalRecipients, setAdditionalRecipients] = useState(item?.AdditionalRecipients ?? "");
  // Schedule owner (AdminUser): runs with this user's permissions, receives the email.
  // Editable by SuperAdmin only; everyone else keeps the existing/current user.
  const [adminUser, setAdminUser] = useState<string>(String(item?.AdminUser ?? cache?.UserInfo?.UserID ?? 0));

  const effectiveReportName = fixedReportName ?? reportName;

  // Column-chooser context. When not opened from a grid (standalone create/edit page),
  // synthesize it from the session cache by report name so the Edit modal offers the
  // exact same column checklist as the grid's Schedule modal. Uses the global
  // ColumnInfo (no per-page ColumnDescJson overlay — headers/tooltips only).
  const effectiveGridColumns = useMemo<ScheduleGridColumns | undefined>(() => {
    if (gridColumns) return gridColumns;
    if (!cache?.PageInfo?.Page || !effectiveReportName) return undefined;
    const nameLower = effectiveReportName.toLowerCase();
    let page: { RowID: number } | undefined;
    for (const p of cache.PageInfo.Page) {
      if (p.Name?.toLowerCase() !== nameLower) continue;
      if (!p.Enabled || !p.Available || !OPERATION_TYPES.has(p.Type?.toUpperCase())) continue;
      page = p;
    }
    if (!page) return undefined;
    const data = cache.PageInfo.Data?.find(d => d.RowID === page!.RowID);
    const rawCols = data?.Columns as unknown;
    const columns = Array.isArray(rawCols) ? (rawCols as string[]).filter(Boolean)
      : typeof rawCols === "string" ? rawCols.split(",").map(s => s.trim()).filter(Boolean)
      : [];
    if (columns.length === 0) return undefined;
    const hidden = new Set((data?.HiddenColumns ?? []).map(c => c.toLowerCase()));
    const defaultVisible = columns.filter(c => !hidden.has(c.toLowerCase()));
    const displayNames: Record<string, string> = {};
    const tooltips: Record<string, string> = {};
    const groups: Record<string, string[]> = {};
    const present = new Set(columns.map(c => c.toLowerCase()));

    // Per-column descriptor, same precedence as DataGrid's colInfoMap: global
    // cache.ColumnInfo, then page ColumnDescJson, then pivot ColumnDescJson —
    // the page/pivot blobs are WHOLE-COLUMN replacements (a column present there
    // is fully described by it, no per-field fallback to the global row).
    const desc = new Map<string, { Header?: string; ToolTip?: string; DisplayType?: string; LinkView?: string }>();
    for (const ci of cache.ColumnInfo ?? []) {
      if (ci.ColumnName) desc.set(ci.ColumnName.toLowerCase(), ci);
    }
    const overlay = (json: string | undefined) => {
      for (const [key, partial] of Object.entries(parseColumnDescJson(json))) {
        desc.set(key, {
          Header: partial.Header == null ? "" : String(partial.Header),
          ToolTip: partial.ToolTip == null ? "" : String(partial.ToolTip),
          DisplayType: partial.DisplayType == null ? "" : String(partial.DisplayType),
          LinkView: partial.LinkView == null ? "" : String(partial.LinkView),
        });
      }
    };
    const pageDataObj = data as { ColumnDescJson?: string } | undefined;
    const pivot = cache.PageInfo.Pivot?.find(p => p.RowID === page!.RowID) as { ColumnDescJson?: string } | undefined;
    overlay(pageDataObj?.ColumnDescJson);
    overlay(pivot?.ColumnDescJson);

    for (const c of columns) {
      const ci = desc.get(c.toLowerCase());
      if (!ci) continue;
      if (ci.Header) displayNames[c.toLowerCase()] = ci.Header;
      if (ci.ToolTip) tooltips[c.toLowerCase()] = ci.ToolTip;
      if ((ci.DisplayType ?? "").toUpperCase() === "TEXTCOLLECTION" && ci.LinkView) {
        const subs: string[] = [];
        const fields = ci.LinkView.split(/[¤§]/);
        for (let i = 1; i < fields.length; i++) {
          const sub = (fields[i]?.split("|")[2] ?? "").trim();
          if (sub && sub.toLowerCase() !== c.toLowerCase() && present.has(sub.toLowerCase()) && !subs.some(s => s.toLowerCase() === sub.toLowerCase())) {
            subs.push(sub);
          }
        }
        if (subs.length > 0) groups[c] = subs;
      }
    }
    return { columns, visible: defaultVisible, defaultVisible, displayNames, tooltips, groups };
  }, [gridColumns, cache, effectiveReportName]);

  // Resolve the report's page-data row ONCE, with the SAME criteria the column
  // synthesis above uses (enabled + available + an operation type, last match) —
  // then hand it to ScheduleFilterEditor. Without this, the filter editor did its
  // own looser name-only lookup and, when a report name is reused across modules,
  // loaded a different page's filters than the columns. Falls back to any
  // same-named page so filters aren't lost if none matches the strict criteria.
  const reportPageData = useMemo<WebAdminPageData | undefined>(() => {
    if (!cache?.PageInfo?.Page || !effectiveReportName) return undefined;
    const nameLower = effectiveReportName.toLowerCase();
    let page: { RowID: number } | undefined;
    for (const p of cache.PageInfo.Page) {
      if (p.Name?.toLowerCase() !== nameLower) continue;
      if (!p.Enabled || !p.Available || !OPERATION_TYPES.has(p.Type?.toUpperCase())) continue;
      page = p;
    }
    const rowId = page?.RowID ?? cache.PageInfo.Page.find(p => p.Name?.toLowerCase() === nameLower)?.RowID;
    if (rowId == null) return undefined;
    return cache.PageInfo.Data?.find(d => d.RowID === rowId) as WebAdminPageData | undefined;
  }, [cache?.PageInfo, effectiveReportName]);

  // Schedule fields
  const initialSchedule = useMemo(() => parseSchedule(item?.Schedule ?? "", item?.Interval ?? "monthly"), [item?.Schedule, item?.Interval]);
  const [interval, setInterval] = useState(item?.Interval || "monthly");
  const [hour, setHour] = useState(initialSchedule.hour);
  const [day, setDay] = useState(initialSchedule.day);
  const [weekday, setWeekday] = useState(initialSchedule.weekday);
  const [month, setMonth] = useState(initialSchedule.month);
  const [offset, setOffset] = useState(initialSchedule.offset);
  const [mask, setMask] = useState(initialSchedule.mask);
  // Schedule timezone. On a brand-new schedule (no persisted id) default to the
  // client's IANA zone when the feature is enabled — so creating one starts in
  // DST-aware mode pre-filled with "their" timezone. Empty = legacy UTC-offset.
  const [scheduleTz, setScheduleTz] = useState<string>(
    item?.ScheduleTimeZone ?? (!item?.ID && tzEditEnabled ? getClientTimeZone() : ""),
  );

  // Export settings
  const [culture, setCulture] = useState(parsedArgs?.Culture ?? "en-US");
  const [fileType, setFileType] = useState<ExportDataFileType>(parsedArgs?.FileType ?? "CSV");
  const [timezone, setTimezone] = useState(parsedArgs?.Timezone ?? getClientTimeZone());

  // Columns to export. A stored selection (editing) wins; otherwise default to
  // NONE selected — an empty list exports the report's default columns server-side,
  // so we don't pre-check anything (avoids implying a frozen column set).
  const [columnList, setColumnList] = useState<string[]>(
    () => parsedArgs?.ColumnList ?? [],
  );
  const [columnsOpen, setColumnsOpen] = useState(false);

  // When the report changes, reset the column selection: an existing schedule's
  // stored list (parsedArgs.ColumnList) belongs to its own report, so we keep it
  // when that report loads, but otherwise clear to NONE (empty = report defaults).
  // We no longer pre-fill the grid/report default columns.
  const prevReportRef = useRef(effectiveReportName);
  useEffect(() => {
    if (prevReportRef.current === effectiveReportName) return;
    prevReportRef.current = effectiveReportName;
    setColumnList(parsedArgs?.ColumnList ?? []);
  }, [effectiveReportName, parsedArgs?.ColumnList]);

  // Raw JSON toggle
  const [showRawJson, setShowRawJson] = useState(false);
  const [rawExportArgs, setRawExportArgs] = useState(item?.ExportArgs ?? "");

  // Filter state
  const editedFiltersRef = useRef<InputParamSE[]>(parsedArgs?.FilterList ?? initialGridFilters ?? []);
  const handleFiltersChange = useCallback((f: InputParamSE[]) => { editedFiltersRef.current = f; }, []);

  const initialFilters = useMemo<InputParam[]>(
    () => parsedArgs?.FilterList ?? initialGridFilters ?? [],
    [parsedArgs?.FilterList, initialGridFilters],
  );

  // Sync when item changes (e.g. after save returns updated data)
  useEffect(() => {
    if (!item) return;
    setTitle(item.Title);
    if (!fixedReportName) setReportName(item.ReportName);
    setAdditionalRecipients(item.AdditionalRecipients ?? "");
    setAdminUser(String(item.AdminUser ?? cache?.UserInfo?.UserID ?? 0));
    setInterval(item.Interval || "monthly");
    const s = parseSchedule(item.Schedule, item.Interval);
    setHour(s.hour); setDay(s.day); setWeekday(s.weekday); setMonth(s.month); setOffset(s.offset);
    setMask(s.mask); setScheduleTz(item.ScheduleTimeZone ?? (!item.ID && tzEditEnabled ? getClientTimeZone() : ""));
    const a = parseExportArgs(item.ExportArgs);
    setCulture(a.Culture); setFileType(a.FileType); setTimezone(a.Timezone);
    setColumnList(a.ColumnList ?? []);
    setRawExportArgs(item.ExportArgs);
    // Deps intentionally EXCLUDE effectiveGridColumns: this effect (re)loads the
    // item's stored values, and must not re-run when the column context changes
    // on report selection — doing so reset the user's just-picked report (columns
    // and filters flashed in, then vanished). Column defaults for the chosen
    // report are applied by the prevReport effect above.
  }, [item, fixedReportName, cache?.UserInfo?.UserID, tzEditEnabled]);

  // Schedule-timezone picker options: IANA ids, plus the current value if it
  // isn't a known IANA id (so a legacy/Windows-named stored zone still shows).
  const scheduleTzOptions = useMemo<{ value: string; label: string }[]>(() => {
    const base = IANA_ZONES.map(z => ({ value: z, label: z }));
    return scheduleTz && !IANA_ZONES.includes(scheduleTz)
      ? [{ value: scheduleTz, label: scheduleTz }, ...base]
      : base;
  }, [scheduleTz]);

  // Report (output) timezone picker options — shared with the Export Grid dialog.

  const reportTzOptions = useMemo(() => buildReportTimezoneOptions(cache, timezone), [cache, timezone]);

  const handleSave = useCallback(() => {
    // Guard: server rejects a weekdays schedule whose mask selects no days.
    if (interval === "weekdays" && !mask.includes("1")) return;
    const schedule = buildSchedule(interval, hour, day, weekday, month, offset, mask);

    let exportArgs: string;
    if (showRawJson) {
      exportArgs = rawExportArgs;
    } else {
      // Match the legacy WebAdmin2 ExportArgs shape: omit OrderList when empty
      // and drop any null-valued property (e.g. `oValue: null` on Instructions-
      // driven date filters). Server's RunScheduledExport parses what's
      // present; explicit nulls are noise the older form never emitted.
      const orderList = parsedArgs?.OrderList ?? initialGridSorts ?? [];
      const payload: Record<string, unknown> = {
        Name: fixedReportName ?? reportName,
        FilterList: editedFiltersRef.current,
        Culture: culture,
        Timezone: timezone,
        FileType: fileType,
        // Worker builds the export POST URL + emailed download link from the
        // origin the schedule was saved from. Captured automatically.
        Origin: window.location.origin,
      };
      if (orderList.length > 0) payload.OrderList = orderList;
      // §6.3 wire protocol: store the explicit column selection only when it differs
      // from the report's default visible set (case-insensitive set equality, known
      // only when opened from a grid). A default-equal or empty selection omits
      // ColumnList — the server then synthesizes the report's default export columns
      // (memoized exportDefault keep-set). Chips/edit flows without grid context have
      // no default to compare against, so their stored lists stay explicit.
      const isDefaultColumns = (() => {
        const def = effectiveGridColumns?.defaultVisible;
        if (!def || columnList.length !== def.length) return false;
        const s = new Set(def.map(c => c.toLowerCase()));
        return columnList.every(c => s.has(c.toLowerCase()));
      })();
      if (columnList.length > 0 && !isDefaultColumns) payload.ColumnList = columnList;
      exportArgs = JSON.stringify(payload, (_key, val) => val === null ? undefined : val);
    }

    const fallbackOwner = item?.AdminUser ?? cache?.UserInfo?.UserID ?? 0;
    const parsedOwner = parseInt(adminUser, 10);
    onSave({
      ID: item?.ID ?? 0,
      Title: title,
      Interval: interval,
      Schedule: schedule,
      ScheduleTimeZone: scheduleTz || null,
      ComputedNextRun: item?.ComputedNextRun ?? null,
      AdminUser: isSuperAdmin && Number.isFinite(parsedOwner) && parsedOwner > 0 ? parsedOwner : fallbackOwner,
      ReportName: fixedReportName ?? reportName,
      ExportArgs: exportArgs,
      AdditionalRecipients: additionalRecipients || "",
      LastRun: item?.LastRun || null,
      NextRun: item?.NextRun || null,
      Created: item?.Created || null,
      Removed: item?.Removed || null,
      Suspended: item?.Suspended || null,
      SuspendedReason: item?.SuspendedReason ?? "",
      CalcTS: item?.CalcTS ?? 0,
    });
  }, [item, title, reportName, fixedReportName, interval, hour, day, weekday, month, offset, mask, scheduleTz,
    culture, fileType, timezone, additionalRecipients, adminUser, isSuperAdmin, showRawJson, rawExportArgs, columnList,
    effectiveGridColumns?.defaultVisible, parsedArgs?.OrderList, initialGridSorts, cache?.UserInfo?.UserID, onSave]);

  useImperativeHandle(ref, () => ({ submit: handleSave }), [handleSave]);

  const dis = saving || disabled;

  // Run-time time basis: a non-empty schedule timezone means "zone" mode (DST-aware),
  // empty means "offset" mode (legacy fixed UTC offset). The toggle flips between them;
  // it's shown when the feature is enabled, or locked-on when an existing schedule
  // already carries a zone but the flag is off (read-only, still visible).
  const scheduleMode: "offset" | "zone" = scheduleTz !== "" ? "zone" : "offset";
  const showTzToggle = tzEditEnabled || scheduleMode === "zone";

  return (
    <div className={styles.exportForm}>
     <div className={colStyles.formLayout}>
      <div className={colStyles.formCol}>
      {/* Identity / delivery — two-column grid with minimum widths */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: "var(--space-3)" }}>
        <label className={styles.exportLabel} style={{ gridColumn: "1 / -1" }}>
          Title
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Export title" className={styles.exportSelect} disabled={dis} />
        </label>

        <label className={styles.exportLabel} style={{ gridColumn: "1 / -1" }}>
          Report
          {fixedReportName ? (
            <input type="text" value={availableReports.find(r => r.value === effectiveReportName)?.label ?? effectiveReportName} className={styles.exportSelect} disabled readOnly />
          ) : (
            <ReportSearchSelect
              reports={availableReports}
              value={effectiveReportName}
              onChange={setReportName}
              disabled={dis}
            />
          )}
        </label>

        <label className={styles.exportLabel} style={{ gridColumn: "1 / -1" }}>
          Additional Recipients
          <input type="text" value={additionalRecipients} onChange={e => setAdditionalRecipients(e.target.value)} placeholder="email1@example.com, email2@example.com" className={styles.exportSelect} disabled={dis} />
        </label>

        {isSuperAdmin && (
          <label className={styles.exportLabel} style={{ gridColumn: "1 / -1" }} title="The schedule runs with this user's permissions and the export email goes to this user.">
            Owner User ID (SuperAdmin)
            <input type="number" value={adminUser} onChange={e => setAdminUser(e.target.value)} className={styles.exportSelect} disabled={dis} min={1} />
          </label>
        )}
      </div>

      {/* ── Run time — when the export fires ─────────────────────────── */}
      <fieldset className={colStyles.group}>
        <legend className={colStyles.groupLegend}>Run time</legend>
        <div className={colStyles.groupGrid}>
          <label className={styles.exportLabel}>
            Interval
            <select className={styles.exportSelect} value={interval} onChange={e => setInterval(e.target.value)} disabled={dis}>
              {INTERVALS
                .filter(i => i.value !== "weekdays" || weekdaysEnabled || interval === "weekdays")
                .map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
          </label>

          {interval === "yearly" ? (
            <label className={styles.exportLabel}>
              Month
              <select className={styles.exportSelect} value={month} onChange={e => setMonth(e.target.value)} disabled={dis}>
                {Array.from({ length: 12 }, (_, i) => { const v = String(i + 1).padStart(2, "0"); return <option key={v} value={v}>{v}</option>; })}
              </select>
            </label>
          ) : interval === "weekly" ? (
            <label className={styles.exportLabel}>
              Day of Week
              <select className={styles.exportSelect} value={weekday} onChange={e => setWeekday(e.target.value)} disabled={dis}>
                {WEEKDAYS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
              </select>
            </label>
          ) : interval === "weekdays" ? (
            <div className={styles.exportLabel} style={{ gridColumn: "1 / -1" }}>
              Weekdays
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
                {MASK_DAYS.map((d, i) => (
                  <label key={d} style={{ display: "flex", alignItems: "center", gap: "var(--space-0-5)", cursor: (dis || !weekdaysEnabled) ? "default" : "pointer", fontWeight: "var(--font-normal)" }}>
                    <input
                      type="checkbox"
                      checked={mask[i] === "1"}
                      disabled={dis || !weekdaysEnabled}
                      onChange={() => { const next = mask.split(""); next[i] = next[i] === "1" ? "0" : "1"; setMask(next.join("")); }}
                    />
                    {d}
                  </label>
                ))}
              </div>
              {weekdaysEnabled && !mask.includes("1") && (
                <span style={{ fontSize: "var(--text-xs)", color: "var(--color-error)" }}>
                  Select at least one weekday to save.
                </span>
              )}
              {!weekdaysEnabled && (
                <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                  Weekday scheduling is not enabled for this database.
                </span>
              )}
            </div>
          ) : <div />}

          {(interval === "monthly" || interval === "yearly") && (
            <label className={styles.exportLabel}>
              Day
              <select className={styles.exportSelect} value={day} onChange={e => setDay(e.target.value)} disabled={dis}>
                {Array.from({ length: 28 }, (_, i) => { const v = String(i + 1).padStart(2, "0"); return <option key={v} value={v}>{v}</option>; })}
              </select>
            </label>
          )}

          <label className={styles.exportLabel}>
            Hour (0-23)
            <input type="text" value={hour} onChange={e => setHour(e.target.value)} placeholder="05" className={styles.exportSelect} disabled={dis} />
          </label>

          {/* Time basis — UTC offset vs DST-aware schedule timezone */}
          <div className={styles.exportLabel} style={{ gridColumn: "1 / -1" }}>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-2)", flexWrap: "wrap" }}>
              <span>Time basis</span>
              {showTzToggle && (
                <span className={colStyles.seg} role="group" aria-label="Schedule time basis">
                  <button
                    type="button"
                    className={[colStyles.segBtn, scheduleMode === "offset" ? colStyles.segBtnActive : ""].filter(Boolean).join(" ")}
                    disabled={dis || !tzEditEnabled}
                    aria-pressed={scheduleMode === "offset"}
                    onClick={() => setScheduleTz("")}
                  >
                    UTC Offset
                  </button>
                  <button
                    type="button"
                    className={[colStyles.segBtn, scheduleMode === "zone" ? colStyles.segBtnActive : ""].filter(Boolean).join(" ")}
                    disabled={dis || !tzEditEnabled}
                    aria-pressed={scheduleMode === "zone"}
                    onClick={() => setScheduleTz(scheduleTz || getClientTimeZone())}
                  >
                    Schedule timezone
                  </button>
                </span>
              )}
            </span>



            {scheduleMode === "offset" ? (
              <input
                type="text"
                value={offset}
                onChange={e => setOffset(e.target.value)}
                placeholder="+02"
                className={styles.exportSelect}
                disabled={dis}
                aria-label="UTC offset"
              />
            ) : (
              <SearchableSelect
                options={scheduleTzOptions}
                value={scheduleTz}
                onChange={setScheduleTz}
                disabled={dis || !tzEditEnabled}
                ariaLabel="Schedule timezone"
                placeholder="Search timezone…"
              />
            )}
            {scheduleMode === "zone" && (
              <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)" }}>
                Fires at local wall-clock time in {scheduleTz}; adjusts for DST automatically.
              </span>
            )}
            {scheduleMode === "zone" && !tzEditEnabled && (
              <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                Timezone editing is not enabled for this database.
              </span>
            )}
          </div>
        </div>
      </fieldset>

      {/* ── Output — how the produced file is rendered ───────────────── */}
      <fieldset className={colStyles.group}>
        <legend className={colStyles.groupLegend}>Output</legend>
        <div className={colStyles.groupGrid}>
          <label className={styles.exportLabel} style={{ gridColumn: "1 / -1" }}>
            Timezone
            <SearchableSelect
              options={reportTzOptions}
              value={timezone}
              onChange={setTimezone}
              disabled={dis}
              ariaLabel="Report timezone"
              placeholder="Search timezone…"
            />
            
          </label>

          <label className={styles.exportLabel} style={{ gridColumn: "1 / -1" }}>
            Culture
            <SearchableSelect
              options={CULTURES}
              value={culture}
              onChange={setCulture}
              disabled={dis}
              ariaLabel="Culture"
              placeholder="Search cultures…"
            />
           
          </label>

          <label className={styles.exportLabel} style={{ gridColumn: "1 / -1" }}>
            File Type
            <select className={styles.exportSelect} value={fileType} onChange={e => setFileType(e.target.value as ExportDataFileType)} disabled={dis}>
              {FILE_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
            </select>
          </label>
        </div>
      </fieldset>
      </div>

      <div className={colStyles.formCol}>
      {/* Filters section */}
      <fieldset style={{
        border: "1px solid var(--color-border-default)",
        borderRadius: "var(--radius-2)",
        padding: "var(--space-2) var(--space-3)",
        margin: 0,
        // Override the UA fieldset `min-inline-size: min-content` so the panel
        // shrinks to the modal width instead of overflowing on narrow screens.
        minWidth: 0,
      }}>
        <legend style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-medium)", padding: "0 var(--space-1)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          Report Filters
          {isSuperAdmin && (
            <label style={{ fontSize: "var(--text-xs)", fontWeight: "var(--font-normal)", cursor: "pointer", display: "flex", alignItems: "center", gap: "var(--space-0-5)" }}>
              <input type="checkbox" checked={showRawJson} onChange={e => setShowRawJson(e.target.checked)} />
              Raw JSON
            </label>
          )}
        </legend>

        {showRawJson ? (
          <textarea
            value={rawExportArgs}
            onChange={e => setRawExportArgs(e.target.value)}
            rows={6}
            disabled={dis}
            style={{
              width: "100%",
              fontFamily: "var(--font-family-input-multiline-mono)",
              fontSize: "var(--text-xs)",
              padding: "var(--space-2)",
              border: "1px solid var(--color-border-default)",
              borderRadius: "var(--radius-1)",
              background: "var(--color-bg)",
              color: "var(--color-text)",
              resize: "vertical",
            }}
          />
        ) : (
          cache && effectiveReportName && (
            <ScheduleFilterEditor
              pageData={reportPageData}
              initialFilters={initialFilters}
              cache={cache}
              onChange={handleFiltersChange}
            />
          )
        )}
      </fieldset>

      {/* Columns section — checklist whenever chooser context is available (passed from
          the grid, or synthesized from the cache by report name on the standalone page);
          read-only chips as last resort (report unknown/no permission). Hidden in Raw
          JSON mode (ColumnList is edited as part of the JSON). */}
      {!showRawJson && (effectiveGridColumns ? (
        <details className={colStyles.columnsSection} open={columnsOpen} onToggle={e => setColumnsOpen((e.target as HTMLDetailsElement).open)}>
          <summary className={colStyles.columnsSummary}>
            Columns
            <span className={colStyles.columnsCount}>
              {columnList.length} of {effectiveGridColumns.columns.length} selected
              {columnList.length === 0 && " — none selected, the report's default columns are exported"}
            </span>
          </summary>
          <div className={colStyles.columnsBody}>
            <ColumnConfig
              columns={effectiveGridColumns.columns}
              visible={columnList}
              defaultVisible={effectiveGridColumns.defaultVisible}
              onChange={setColumnList}
              displayNames={effectiveGridColumns.displayNames}
              tooltips={effectiveGridColumns.tooltips}
              groups={effectiveGridColumns.groups}
            />
          </div>
        </details>
      ) : columnList.length > 0 ? (
        <div className={colStyles.columnsSection}>
          <div className={colStyles.columnsSummary}>
            Columns
            <span className={colStyles.columnsCount}>{columnList.length} selected</span>
          </div>
          <div className={colStyles.chipList}>
            {columnList.map(col => (
              <span key={col} className={colStyles.chip}>
                {col}
                {!dis && (
                  <button
                    type="button"
                    className={colStyles.chipRemove}
                    aria-label={`Remove column ${col}`}
                    title={`Remove column ${col}`}
                    onClick={() => setColumnList(columnList.filter(c => c !== col))}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
          <div className={colStyles.columnsNote}>Open the schedule from its grid to add columns.</div>
        </div>
      ) : null)}

      {/* Status (existing items only) */}
      {showStatus && item && (item.LastRun || item.NextRun || item.ComputedNextRun || item.Suspended) && (
        <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", display: "flex", flexWrap: "wrap", gap: "var(--space-4)" }}>
          {item.LastRun && <span>Last Run: {item.LastRun}</span>}
          {(item.ComputedNextRun ?? item.NextRun) && <span>Next Run: {item.ComputedNextRun ?? item.NextRun}</span>}
          {item.Suspended && <span>Suspended: {item.Suspended}{item.SuspendedReason ? ` — ${item.SuspendedReason}` : ""}</span>}
        </div>
      )}
      </div>
     </div>
    </div>
  );
});
